import React, { useEffect, useState } from "react";
import {
  Globe,
  Upload, 
  FolderPlus,
  Layers3,
  CheckCircle2,
  Palette,
  ArrowRight,
  X,
  Maximize2
} from "lucide-react";
import "./RequestProj.css";
import {
  getMessageTemplates,
  getDesignTemplates,
  submitProjectRequest,
} from "../../../services/RequestProjectService";
import { toast } from "react-toastify";


function RequestProject() {
  const [templates, setTemplates] = useState([]);
  const [designs, setDesigns] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Modal State for Big View
  const [activeModalDesign, setActiveModalDesign] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    base_url: "",
    sender_name: "",
    sender_email: "",
  });

  // Track validation error messages for the form fields
  const [formErrors, setFormErrors] = useState({});
  
  // New distinct state hooks specifically for file validation and previews
  const [logoFile, setLogoFile] = useState(null);
  const [logoLocalPreviewUrl, setLogoLocalPreviewUrl] = useState("");

  const [selectedTemplates, setSelectedTemplates] = useState([]);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await getMessageTemplates();
      setTemplates(response);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    // Clear the error message for this specific field dynamically as the user types
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Intercept the native file upload trigger
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      setLogoLocalPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleTemplateSelect = async (template) => {
    const exists = selectedTemplates.find((item) => item.template_id === template.id);

    if (exists) {
      setSelectedTemplates(selectedTemplates.filter((item) => item.template_id !== template.id));
      return;
    }

    try {
      const response = await getDesignTemplates(template.id);
      setDesigns((prev) => ({ ...prev, [template.id]: response }));

      setSelectedTemplates([
        ...selectedTemplates,
        {
          template_id: template.id,
          template_name: template.name,
          design_id: null,
          primary_color: "#4f7df3", 
        },
      ]);
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch design templates.");
    }
  };

  const handleDesignSelect = (templateId, designId, e) => {
    if (e) e.stopPropagation();
    
    setSelectedTemplates(
      selectedTemplates.map((item) =>
        item.template_id === templateId ? { ...item, design_id: designId } : item
      )
    );
  };

  // Process data to inject dynamic parameters cleanly into structural HTML strings
  const getRenderedHTML = (htmlContent, templateColor) => {
    if (!htmlContent) return "";
    
    const activeLogoUrl = logoLocalPreviewUrl || "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=200&auto=format&fit=crop&q=60";
    const primaryColor = templateColor || "#4f7df3";

    let baseHTML = htmlContent;
    if (!htmlContent.includes("<html") && !htmlContent.includes("<body")) {
      baseHTML = `<html><body style="margin:0; padding:20px; font-family:sans-serif; background:#ffffff; color:#333333;">${htmlContent}</body></html>`;
    }

    return baseHTML
      .replaceAll("{{primary_color}}", primaryColor)
      .replaceAll("{{logo_url}}", activeLogoUrl)
      .replaceAll("{{project_name}}", formData.name || "Demo Application")
      .replaceAll("{{otp_code}}", "582194")
      .replaceAll("{{amount}}", "₹1,499.00")
      .replaceAll("{{date}}", "May 18, 2026")
      .replaceAll("{{username}}", "alex_p")
      .replaceAll("{{password}}", "Tr@nsForm2026!")
      .replaceAll("{{login_url}}", "#");
  };

  // Form Field Validation Rules Handler
  const validateForm = () => {
    const errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    // Only allows letters and spaces (No numbers or special characters)
    const nameRegex = /^[a-zA-Z\s]+$/;

    if (!formData.name || !formData.name.trim()) {
      errors.name = "Project name is required.";
    } else if (!nameRegex.test(formData.name)) {
      errors.name = "Numbers and special characters are not allowed.";
    }

    if (!formData.sender_name || !formData.sender_name.trim()) {
      errors.sender_name = "Sender name is required.";
    }

    if (!formData.sender_email || !formData.sender_email.trim()) {
      errors.sender_email = "Sender email is required.";
    } else if (!emailRegex.test(formData.sender_email)) {
      errors.sender_email = "Please enter a valid email address.";
    }

    if (!selectedTemplates.length) {
      errors.templates = "Please select at least one message hook template.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    // 1. Run standard form validations first
    const isFormValid = validateForm();
    if (!isFormValid) {
      return;
    }

    // 2. Check layout assignments for selected configurations
    const missingLayouts = selectedTemplates.some((item) => !item.design_id);
    if (missingLayouts) {
      toast.error("Please select designs for all templates");
      // alert("Please select designs for all templates");
      return;
    }

    const submissionPayload = new FormData();
    submissionPayload.append("name", formData.name);
    submissionPayload.append("base_url", formData.base_url);
    submissionPayload.append("sender_name", formData.sender_name);
    submissionPayload.append("sender_email", formData.sender_email);
    
    if (logoFile) {
      submissionPayload.append("logo_img", logoFile); 
    }
    
    submissionPayload.append("templates", JSON.stringify(selectedTemplates));

    try {
      setSubmitting(true);
      const response = await submitProjectRequest(submissionPayload);
      toast.success(response.message || "Project submitted successfully!");
      
      // Cleanup States completely
      setFormData({ name: "", base_url: "", sender_name: "", sender_email: "" });
      setFormErrors({});
      setLogoFile(null);
      setLogoLocalPreviewUrl("");
      setSelectedTemplates([]);
    } catch (error) {
      toast.error(error.message || "An error occurred during template build processing.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="request-project-page">
      {/* HEADER */}
      <div className="request-header">
        <div>
          <h1>Request New Project</h1>
          <p>Configure your communication project and select layouts cleanly</p>
        </div>
        <div className="request-badge">
          <FolderPlus size={18} />
          Project Request
        </div>
      </div>

      {/* FORM CONFIGURATION FIELDS */}
      <div className="request-layout">
        <div className="request-left-card">
          <div className="section-header">
            <h2>Project Details</h2>
          </div>

          <div className="form-group">
            <label>Project Name *</label>
            <div className={`input-box ${formErrors.name ? "input-box-error" : ""}`}>
              <FolderPlus size={18} />
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter project name (e.g., Ignis Vigil)"
              />
            </div>
            {formErrors.name && (
              <span className="error-text-message" style={{ color: "red", fontSize: "12px", display: "block", marginTop: "4px" }}>
                {formErrors.name}
              </span>
            )}
          </div>

          <div className="form-group">
            <label>Base URL</label>
            <div className="input-box">
              <Globe size={18} />
              <input
                type="text"
                name="base_url"
                value={formData.base_url}
                onChange={handleInputChange}
                placeholder="https://example.com"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Sender Name *</label>
            <div className={`input-box ${formErrors.sender_name ? "input-box-error" : ""}`}>
              <input
                type="text"
                name="sender_name"
                value={formData.sender_name}
                onChange={handleInputChange}
                placeholder="Enter sender name"
              />
            </div>
            {formErrors.sender_name && (
              <span className="error-text-message" style={{ color: "red", fontSize: "12px", display: "block", marginTop: "4px" }}>
                {formErrors.sender_name}
              </span>
            )}
          </div>

          <div className="form-group">
            <label>Sender Email *</label>
            <div className={`input-box ${formErrors.sender_email ? "input-box-error" : ""}`}>
              <input
                type="email"
                name="sender_email"
                value={formData.sender_email}
                onChange={handleInputChange}
                placeholder="Enter sender email"
              />
            </div>
            {formErrors.sender_email && (
              <span className="error-text-message" style={{ color: "red", fontSize: "12px", display: "block", marginTop: "4px" }}>
                {formErrors.sender_email}
              </span>
            )}
          </div>

          {/* IMAGE FIELD WRAPPER */}
          <div className="form-group">
            <label>Upload Brand Logo</label>
            <div className="file-upload-wrapper">
              <label htmlFor="logo-file-input" className="file-upload-dropzone">
                <Upload size={20} className="upload-icon" />
                <div className="dropzone-text-group">
                  <span className="dropzone-title">
                    {logoFile ? logoFile.name : "Choose local image file..."}
                  </span>
                  <span className="dropzone-subtitle">Supports: PNG, JPG, JPEG, SVG</span>
                </div>
              </label>
              <input
                id="logo-file-input"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
              {logoLocalPreviewUrl && (
                <div className="logo-thumbnail-preview-badge">
                  <img src={logoLocalPreviewUrl} alt="Local logo preview look" />
                  <button 
                    type="button" 
                    className="clear-file-btn" 
                    onClick={() => { setLogoFile(null); setLogoLocalPreviewUrl(""); }}
                  >
                    <X size={14} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="request-right-card">
          <div className="section-header">
            <h2>Selected Checklists</h2>
          </div>

          {selectedTemplates.length === 0 ? (
            <div className="empty-state">
              <Layers3 size={45} />
              <p>No template selected yet</p>
              {formErrors.templates && (
                <span style={{ color: "red", fontSize: "12px", fontWeight: "500", marginTop: "8px" }}>
                  {formErrors.templates}
                </span>
              )}
            </div>
          ) : (
            selectedTemplates.map((item) => (
              <div key={item.template_id} className="selected-template-card">
                <h4>{item.template_name}</h4>
                <span className={item.design_id ? "status-filled" : "status-empty"}>
                  {item.design_id ? "✓ Layout Wired" : "⚠️ Needs Design Layout"}
                </span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* MESSAGE TOP LEVEL SELECTION */}
      <div className="template-selection-card">
        <div className="section-header">
          <h2>Select Message Dynamic Hooks</h2>
        </div>

        {loading ? (
          <p>Loading components dynamically...</p>
        ) : (
          <div className="template-grid">
            {templates.map((template) => {
              const selected = selectedTemplates.find((t) => t.template_id === template.id);
              return (
                <div
                  key={template.id}
                  className={`template-card ${selected ? "active" : ""}`}
                  onClick={() => handleTemplateSelect(template)}
                >
                  <div className="template-top">
                    <Layers3 size={22} />
                    {selected && <CheckCircle2 size={20} className="check-icon" />}
                  </div>
                  <h3>{template.name}</h3>
                  <p className="badge-channel">{template.channel_type || "EMAIL"}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* RENDER DYNAMIC CARD PREVIEW SECTIONS */}
      {selectedTemplates.map((template) => (
        <div className="design-section" key={template.template_id}>
          <div className="section-header">
            <h2>Assign Interactive View Style for: <b style={{color: template.primary_color}}>{template.template_name}</b></h2>
          </div>

          <div className="design-grid">
            {designs[template.template_id]?.map((design) => {
              const isSelected = template.design_id === design.id;
              return (
                <div
                  key={design.id}
                  className={`design-card ${isSelected ? "selected" : ""}`}
                  onClick={(e) => handleDesignSelect(template.template_id, design.id, e)}
                >
                  <div className="design-preview-viewport">
                    <div className="preview-overlay-controls">
                      <button 
                        className="control-icon-btn" 
                        title="View Big Preview Screen"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveModalDesign({
                            ...design,
                            colorContext: template.primary_color
                          });
                        }}
                      >
                        <Maximize2 size={16} />
                        <span>Full Scale</span>
                      </button>
                    </div>

                    {design.body_content ? (
                      <div className="scaled-iframe-container">
                        <iframe
                          srcDoc={getRenderedHTML(design.body_content, template.primary_color)}
                          title={`Preview layout ${design.name}`}
                          sandbox="allow-same-origin"
                          className="embedded-preview-frame"
                        />
                      </div>
                    ) : (
                      <div className="preview-placeholder">
                        <Palette size={36} />
                      </div>
                    )}
                  </div>

                  <div className="design-info">
                    <div className="title-row">
                      <h4>{design.name}</h4>
                      {isSelected && <CheckCircle2 size={16} className="text-success" />}
                    </div>
                    <span className="orientation-tag">
                      {design.orientation === "H" ? "Horizontal Row" : "Vertical Stack"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* SUBMISSION ACTION BAR */}
      <div className="submit-section">
        <button className="submit-project-btn" onClick={handleSubmit} disabled={submitting}>
          {submitting ? "Processing Engine Registry..." : "Deploy Request Sequence"}
          <ArrowRight size={18} />
        </button>
      </div>

      {/* REAL-TIME OVERLAY ZOOM MODAL VIEW */}
      {activeModalDesign && (
        <div className="big-view-modal-backdrop" onClick={() => setActiveModalDesign(null)}>
          <div className="big-view-modal-window" onClick={(e) => e.stopPropagation()}>
            <div className="modal-top-bar">
              <div>
                <h3>{activeModalDesign.name} — Real Scale Look</h3>
                <p>Simulating view parameters running directly inside runtime template buffers.</p>
              </div>
              <button className="close-modal-btn" onClick={() => setActiveModalDesign(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body-frame-canvas">
              <iframe
                srcDoc={getRenderedHTML(activeModalDesign.body_content, activeModalDesign.colorContext)}
                title="Full Screen Active Target Scale Preview"
                sandbox="allow-same-origin"
                className="full-scale-target-iframe"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RequestProject;

// import React, { useEffect, useState } from "react";
// import {
//   Globe,
//   Upload, // Changed from ImageIcon to Upload to represent file choosing better
//   FolderPlus,
//   Layers3,
//   CheckCircle2,
//   Palette,
//   ArrowRight,
//   X,
//   Maximize2
// } from "lucide-react";
// import "./RequestProj.css";
// import {
//   getMessageTemplates,
//   getDesignTemplates,
//   submitProjectRequest,
// } from "../../../services/RequestProjectService";

// function RequestProject() {
//   const [templates, setTemplates] = useState([]);
//   const [designs, setDesigns] = useState({});
//   const [loading, setLoading] = useState(false);
//   const [submitting, setSubmitting] = useState(false);

//   // Modal State for Big View
//   const [activeModalDesign, setActiveModalDesign] = useState(null);

//   const [formData, setFormData] = useState({
//     name: "",
//     base_url: "",
//     sender_name: "",
//     sender_email: "",
//   });
  
//   // New distinct state hooks specifically for file validation and previews
//   const [logoFile, setLogoFile] = useState(null);
//   const [logoLocalPreviewUrl, setLogoLocalPreviewUrl] = useState("");

//   const [selectedTemplates, setSelectedTemplates] = useState([]);

//   useEffect(() => {
//     fetchTemplates();
//   }, []);

//   const fetchTemplates = async () => {
//     try {
//       setLoading(true);
//       const response = await getMessageTemplates();
//       setTemplates(response);
//     } catch (error) {
//       console.error(error);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setFormData((prev) => ({ ...prev, [name]: value }));
//   };

//   // Intercept the native file upload trigger
//   const handleFileChange = (e) => {
//     const file = e.target.files[0];
//     if (file) {
//       setLogoFile(file);
//       // This builds a dynamic blob URL (blob:http://...) enabling the local sandboxed iframes 
//       // to display the image immediately without waiting for a backend round-trip
//       setLogoLocalPreviewUrl(URL.createObjectURL(file));
//     }
//   };

//   const handleTemplateSelect = async (template) => {
//     const exists = selectedTemplates.find((item) => item.template_id === template.id);

//     if (exists) {
//       setSelectedTemplates(selectedTemplates.filter((item) => item.template_id !== template.id));
//       return;
//     }

//     try {
//       const response = await getDesignTemplates(template.id);
//       setDesigns((prev) => ({ ...prev, [template.id]: response }));

//       setSelectedTemplates([
//         ...selectedTemplates,
//         {
//           template_id: template.id,
//           template_name: template.name,
//           design_id: null,
//           primary_color: "#4f7df3", 
//         },
//       ]);
//     } catch (error) {
//       console.error(error);
//     }
//   };

//   const handleDesignSelect = (templateId, designId, e) => {
//     if (e) e.stopPropagation();
    
//     setSelectedTemplates(
//       selectedTemplates.map((item) =>
//         item.template_id === templateId ? { ...item, design_id: designId } : item
//       )
//     );
//   };

//   // Process data to inject dynamic parameters cleanly into structural HTML strings
//   const getRenderedHTML = (htmlContent, templateColor) => {
//     if (!htmlContent) return "";
    
//     // Inject the local blob URL into the frame if a file was chosen; otherwise use fallback branding
//     const activeLogoUrl = logoLocalPreviewUrl || "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=200&auto=format&fit=crop&q=60";
//     const primaryColor = templateColor || "#4f7df3";

//     let baseHTML = htmlContent;
//     if (!htmlContent.includes("<html") && !htmlContent.includes("<body")) {
//       baseHTML = `<html><body style="margin:0; padding:20px; font-family:sans-serif; background:#ffffff; color:#333333;">${htmlContent}</body></html>`;
//     }

//     return baseHTML
//       .replaceAll("{{primary_color}}", primaryColor)
//       .replaceAll("{{logo_url}}", activeLogoUrl)
//       .replaceAll("{{project_name}}", formData.name || "Demo Application")
//       .replaceAll("{{otp_code}}", "582194")
//       .replaceAll("{{amount}}", "₹1,499.00")
//       .replaceAll("{{date}}", "May 18, 2026")
//       .replaceAll("{{username}}", "alex_p")
//       .replaceAll("{{password}}", "Tr@nsForm2026!")
//       .replaceAll("{{login_url}}", "#");
//   };

//   const handleSubmit = async () => {
//     const invalid = selectedTemplates.some((item) => !item.design_id);

//     if (!formData.name ||   !formData.sender_name ||
//   !formData.sender_email || !selectedTemplates.length) {
//       alert("Please fill all required fields");
//       return;
//     }

//     if (invalid) {
//       alert("Please select designs for all templates");
//       return;
//     }

//     // CRITICAL FIX: Convert layout state into flat FormData boundaries for Django storage compatibility
//     const submissionPayload = new FormData();
//     submissionPayload.append("name", formData.name);
//     submissionPayload.append("base_url", formData.base_url);
//     submissionPayload.append("sender_name", formData.sender_name);
//     submissionPayload.append("sender_email", formData.sender_email);
    
//     if (logoFile) {
//       submissionPayload.append("logo_img", logoFile); // Maps directly into Django's models.ImageField
//     }
    
//     // Convert arrays/objects to strings since FormData only handles strings or binary blobs
//     submissionPayload.append("templates", JSON.stringify(selectedTemplates));

//     try {
//       setSubmitting(true);
//       const response = await submitProjectRequest(submissionPayload);
//       alert(response.message || "Project submitted successfully!");
      
//       // Cleanup States completely
//       setFormData({ name: "", base_url: "", sender_name: "", sender_email: "" });
//       setLogoFile(null);
//       setLogoLocalPreviewUrl("");
//       setSelectedTemplates([]);
//     } catch (error) {
//       alert(error.message || "An error occurred during template build processing.");
//     } finally {
//       setSubmitting(false);
//     }
//   };

//   return (
//     <div className="request-project-page">
//       {/* HEADER */}
//       <div className="request-header">
//         <div>
//           <h1>Request New Project</h1>
//           <p>Configure your communication project and select layouts cleanly</p>
//         </div>
//         <div className="request-badge">
//           <FolderPlus size={18} />
//           Project Request
//         </div>
//       </div>

//       {/* FORM CONFIGURATION FIELDS */}
//       <div className="request-layout">
//         <div className="request-left-card">
//           <div className="section-header">
//             <h2>Project Details</h2>
//           </div>

//           <div className="form-group">
//             <label>Project Name *</label>
//             <div className="input-box">
//               <FolderPlus size={18} />
//               <input
//                 type="text"
//                 name="name"
//                 value={formData.name}
//                 onChange={handleInputChange}
//                 placeholder="Enter project name (e.g., Ignis Vigil)"
//               />
//             </div>
//           </div>

//           <div className="form-group">
//             <label>Base URL</label>
//             <div className="input-box">
//               <Globe size={18} />
//               <input
//                 type="text"
//                 name="base_url"
//                 value={formData.base_url}
//                 onChange={handleInputChange}
//                 placeholder="https://example.com"
//               />
//             </div>
//             <div className="form-group">
//   <label>Sender Name </label>
//   <div className="input-box">
//     <input
//       type="text"
//       name="sender_name"
//       value={formData.sender_name}
//       onChange={handleInputChange}
//       placeholder="Enter sender name"
//     />
//   </div>
// </div>

// <div className="form-group">
//   <label>Sender Email </label>
//   <div className="input-box">
//     <input
//       type="email"
//       name="sender_email"
//       value={formData.sender_email}
//       onChange={handleInputChange}
//       placeholder="Enter sender email"
//     />
//   </div>
// </div>
//           </div>

//           {/* UPGRADED IMAGE FIELD WRAPPER */}
//           <div className="form-group">
//             <label>Upload Brand Logo</label>
//             <div className="file-upload-wrapper">
//               <label htmlFor="logo-file-input" className="file-upload-dropzone">
//                 <Upload size={20} className="upload-icon" />
//                 <div className="dropzone-text-group">
//                   <span className="dropzone-title">
//                     {logoFile ? logoFile.name : "Choose local image file..."}
//                   </span>
//                   <span className="dropzone-subtitle">Supports: PNG, JPG, JPEG, SVG</span>
//                 </div>
//               </label>
//               <input
//                 id="logo-file-input"
//                 type="file"
//                 accept="image/*"
//                 onChange={handleFileChange}
//                 style={{ display: "none" }}
//               />
//               {logoLocalPreviewUrl && (
//                 <div className="logo-thumbnail-preview-badge">
//                   <img src={logoLocalPreviewUrl} alt="Local logo preview look" />
//                   <button 
//                     type="button" 
//                     className="clear-file-btn" 
//                     onClick={() => { setLogoFile(null); setLogoLocalPreviewUrl(""); }}
//                   >
//                     <X size={14} />
//                   </button>
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>

//         <div className="request-right-card">
//           <div className="section-header">
//             <h2>Selected Checklists</h2>
//           </div>

//           {selectedTemplates.length === 0 ? (
//             <div className="empty-state">
//               <Layers3 size={45} />
//               <p>No template selected yet</p>
//             </div>
//           ) : (
//             selectedTemplates.map((item) => (
//               <div key={item.template_id} className="selected-template-card">
//                 <h4>{item.template_name}</h4>
//                 <span className={item.design_id ? "status-filled" : "status-empty"}>
//                   {item.design_id ? "✓ Layout Wired" : "⚠️ Needs Design Layout"}
//                 </span>
//               </div>
//             ))
//           )}
//         </div>
//       </div>

//       {/* MESSAGE TOP LEVEL SELECTION */}
//       <div className="template-selection-card">
//         <div className="section-header">
//           <h2>Select Message Dynamic Hooks</h2>
//         </div>

//         {loading ? (
//           <p>Loading components dynamically...</p>
//         ) : (
//           <div className="template-grid">
//             {templates.map((template) => {
//               const selected = selectedTemplates.find((t) => t.template_id === template.id);
//               return (
//                 <div
//                   key={template.id}
//                   className={`template-card ${selected ? "active" : ""}`}
//                   onClick={() => handleTemplateSelect(template)}
//                 >
//                   <div className="template-top">
//                     <Layers3 size={22} />
//                     {selected && <CheckCircle2 size={20} className="check-icon" />}
//                   </div>
//                   <h3>{template.name}</h3>
//                   <p className="badge-channel">{template.channel_type || "EMAIL"}</p>
//                 </div>
//               );
//             })}
//           </div>
//         )}
//       </div>

//       {/* RENDER DYNAMIC CARD PREVIEW SECTIONS */}
//       {selectedTemplates.map((template) => (
//         <div className="design-section" key={template.template_id}>
//           <div className="section-header">
//             <h2>Assign Interactive View Style for: <b style={{color: template.primary_color}}>{template.template_name}</b></h2>
//           </div>

//           <div className="design-grid">
//             {designs[template.template_id]?.map((design) => {
//               const isSelected = template.design_id === design.id;
//               return (
//                 <div
//                   key={design.id}
//                   className={`design-card ${isSelected ? "selected" : ""}`}
//                   onClick={(e) => handleDesignSelect(template.template_id, design.id, e)}
//                 >
//                   <div className="design-preview-viewport">
//                     <div className="preview-overlay-controls">
//                       <button 
//                         className="control-icon-btn" 
//                         title="View Big Preview Screen"
//                         onClick={(e) => {
//                           e.stopPropagation();
//                           setActiveModalDesign({
//                             ...design,
//                             colorContext: template.primary_color
//                           });
//                         }}
//                       >
//                         <Maximize2 size={16} />
//                         <span>Full Scale</span>
//                       </button>
//                     </div>

//                     {design.body_content ? (
//                       <div className="scaled-iframe-container">
//                         <iframe
//                           srcDoc={getRenderedHTML(design.body_content, template.primary_color)}
//                           title={`Preview layout ${design.name}`}
//                           sandbox="allow-same-origin"
//                           className="embedded-preview-frame"
//                         />
//                       </div>
//                     ) : (
//                       <div className="preview-placeholder">
//                         <Palette size={36} />
//                       </div>
//                     )}
//                   </div>

//                   <div className="design-info">
//                     <div className="title-row">
//                       <h4>{design.name}</h4>
//                       {isSelected && <CheckCircle2 size={16} className="text-success" />}
//                     </div>
//                     <span className="orientation-tag">
//                       {design.orientation === "H" ? "Horizontal Row" : "Vertical Stack"}
//                     </span>
//                   </div>
//                 </div>
//               );
//             })}
//           </div>
//         </div>
//       ))}

//       {/* SUBMISSION ACTION BAR */}
//       <div className="submit-section">
//         <button className="submit-project-btn" onClick={handleSubmit} disabled={submitting}>
//           {submitting ? "Processing Engine Registry..." : "Deploy Request Sequence"}
//           <ArrowRight size={18} />
//         </button>
//       </div>

//       {/* REAL-TIME OVERLAY ZOOM MODAL VIEW */}
//       {activeModalDesign && (
//         <div className="big-view-modal-backdrop" onClick={() => setActiveModalDesign(null)}>
//           <div className="big-view-modal-window" onClick={(e) => e.stopPropagation()}>
//             <div className="modal-top-bar">
//               <div>
//                 <h3>{activeModalDesign.name} — Real Scale Look</h3>
//                 <p>Simulating view parameters running directly inside runtime template buffers.</p>
//               </div>
//               <button className="close-modal-btn" onClick={() => setActiveModalDesign(null)}>
//                 <X size={20} />
//               </button>
//             </div>
//             <div className="modal-body-frame-canvas">
//               <iframe
//                 srcDoc={getRenderedHTML(activeModalDesign.body_content, activeModalDesign.colorContext)}
//                 title="Full Screen Active Target Scale Preview"
//                 sandbox="allow-same-origin"
//                 className="full-scale-target-iframe"
//               />
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }

// export default RequestProject;